/**
 * 性能监控 Composable
 * Performance monitoring composable
 */

import { onMounted, onUnmounted } from 'vue'
import { 
  getFirstPaintTime, 
  getResourceTiming, 
  observeLongTasks,
  getNetworkInfo,
  getLoadStrategy
} from '@/utils/performance'

/**
 * 使用性能监控
 * @param {object} options - 配置选项
 */
export function usePerformanceMonitor(options = {}) {
  const {
    enableLongTaskMonitoring = true,
    enableResourceTiming = true,
    logToConsole = process.env.NODE_ENV === 'development'
  } = options
  
  let longTaskObserver = null
  
  onMounted(() => {
    // 记录首屏加载时间
    const paintTime = getFirstPaintTime()
    if (logToConsole && paintTime) {
      console.log(`[Performance] First Paint Time: ${paintTime}ms`)
    }
    
    // 监控长任务
    if (enableLongTaskMonitoring) {
      longTaskObserver = observeLongTasks((entry) => {
        if (logToConsole) {
          console.warn(`[Performance] Long Task Detected:`, {
            name: entry.name,
            duration: `${entry.duration.toFixed(2)}ms`,
            startTime: entry.startTime
          })
        }
      })
    }
    
    // 分析资源加载性能
    if (enableResourceTiming) {
      setTimeout(() => {
        const resources = getResourceTiming()
        
        if (logToConsole && resources.length > 0) {
          // 找出加载最慢的资源
          const slowResources = resources
            .filter(r => r.duration > 1000)
            .sort((a, b) => b.duration - a.duration)
            .slice(0, 5)
          
          if (slowResources.length > 0) {
            console.warn('[Performance] Slow Resources:', slowResources.map(r => ({
              name: r.name,
              duration: `${r.duration.toFixed(2)}ms`,
              size: r.transferSize ? `${(r.transferSize / 1024).toFixed(2)}KB` : 'N/A'
            })))
          }
          
          // 统计总体性能
          const totalDuration = resources.reduce((sum, r) => sum + r.duration, 0)
          const totalSize = resources.reduce((sum, r) => sum + (r.transferSize || 0), 0)
          
          console.log('[Performance] Resource Summary:', {
            count: resources.length,
            totalDuration: `${totalDuration.toFixed(2)}ms`,
            totalSize: `${(totalSize / 1024 / 1024).toFixed(2)}MB`,
            avgDuration: `${(totalDuration / resources.length).toFixed(2)}ms`
          })
        }
      }, 3000)
    }
    
    // 检测网络质量
    const networkInfo = getNetworkInfo()
    if (networkInfo && logToConsole) {
      console.log('[Performance] Network Info:', {
        type: networkInfo.effectiveType,
        downlink: `${networkInfo.downlink}Mbps`,
        rtt: `${networkInfo.rtt}ms`,
        saveData: networkInfo.saveData,
        strategy: getLoadStrategy()
      })
    }
    
    // 监听 Core Web Vitals
    if ('PerformanceObserver' in window) {
      try {
        // Largest Contentful Paint (LCP)
        const lcpObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries()
          const lastEntry = entries[entries.length - 1]
          if (logToConsole) {
            console.log(`[Performance] LCP: ${lastEntry.renderTime || lastEntry.loadTime}ms`)
          }
        })
        lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] })
        
        // First Input Delay (FID)
        const fidObserver = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            if (logToConsole) {
              console.log(`[Performance] FID: ${entry.processingStart - entry.startTime}ms`)
            }
          }
        })
        fidObserver.observe({ entryTypes: ['first-input'] })
        
        // Cumulative Layout Shift (CLS)
        let clsScore = 0
        const clsObserver = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            if (!entry.hadRecentInput) {
              clsScore += entry.value
              if (logToConsole) {
                console.log(`[Performance] CLS Score: ${clsScore.toFixed(4)}`)
              }
            }
          }
        })
        clsObserver.observe({ entryTypes: ['layout-shift'] })
      } catch (e) {
        console.warn('[Performance] Core Web Vitals monitoring not supported', e)
      }
    }
  })
  
  onUnmounted(() => {
    // 清理长任务观察器
    if (longTaskObserver) {
      longTaskObserver.disconnect()
    }
  })
  
  return {
    getFirstPaintTime,
    getResourceTiming,
    getNetworkInfo,
    getLoadStrategy
  }
}

